'use client';

import { useEffect, useEffectEvent } from 'react';

import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import {
  DEFAULT_SIGN_IN_PATH,
  type ForcedSignOutPayload,
  PRIVATE_PATH_PREFIXES,
  handleAuthChange,
} from '../auth-change-handler';
import { consumeSignOutIntent } from '../auth-intent';
import { useSupabase } from './use-supabase';

export type { ForcedSignOutPayload } from '../auth-change-handler';

/**
 * @name useAuthChangeListener
 * @param privatePathPrefixes - A list of private path prefixes
 * @param signInPath - Where to send the user on a forced sign-out (a `?next=`
 *   pointing at the current location is appended)
 * @param onEvent - Callback invoked for every auth event
 * @param onForcedSignOut - Callback invoked when a spurious/forced sign-out is
 *   detected on a private route, used for telemetry (PAY-1006)
 */
export function useAuthChangeListener({
  privatePathPrefixes = PRIVATE_PATH_PREFIXES,
  signInPath = DEFAULT_SIGN_IN_PATH,
  onEvent,
  onForcedSignOut,
}: {
  privatePathPrefixes?: string[];
  signInPath?: string;
  onEvent?: (event: AuthChangeEvent, session: Session | null) => void;
  onForcedSignOut?: (payload: ForcedSignOutPayload) => void;
}) {
  const client = useSupabase();

  const setupAuthListener = useEffectEvent(() => {
    // don't run on the server
    if (typeof window === 'undefined') {
      return;
    }

    // keep this running for the whole session unless the component was unmounted
    return client.auth.onAuthStateChange((event, session) => {
      // The app's side effects (query cache, analytics, Intercom, monitoring)
      // must never cost the user the navigation below. A throw here used to
      // skip handleAuthChange entirely, so a failing third-party script could
      // leave a signed-out user on a private page (PAY-3983).
      try {
        onEvent?.(event, session);
      } catch (error) {
        console.error('[auth] onEvent handler failed', error);
      }

      handleAuthChange(event, {
        pathname: window.location.pathname,
        search: window.location.search,
        privatePathPrefixes,
        signInPath,
        consumeSignOutIntent,
        navigate: (url) => window.location.assign(url),
        onForcedSignOut,
      });
    });
  });

  useEffect(() => {
    const listener = setupAuthListener();

    // destroy listener on un-mounts
    return () => {
      listener?.data.subscription.unsubscribe();
    };
  }, []);
}
